import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaLock, FaUser, FaEye, FaEyeSlash, FaArrowRight } from 'react-icons/fa';

const FranchiseLoginContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const VisualSection = styled(motion.div)`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);

  @media (max-width: 1024px) {
    display: none;
  }
`;

const VisualImage = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: url('https://source.unsplash.com/random/1200x800?business') center/cover;
  opacity: 0.4;
`;

const VisualContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 2rem;
  color: white;
  text-align: center;
  max-width: 80%;
`;

const VisualTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const VisualText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const LoginSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: white;
`;

const LoginCard = styled(motion.div)`
  width: 100%;
  max-width: 450px;
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    padding: 2rem;
    box-shadow: none;
  }
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const LoginTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, #8b5cf6, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const LoginSubtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  color: #4b5563;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.9rem 1rem 0.9rem 2.8rem;
  font-size: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 2.6rem;
  color: #8b5cf6;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 2.6rem;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
`;

const RememberForgot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
`;

const RememberMe = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4b5563;
  cursor: pointer;

  input {
    accent-color: #8b5cf6;
  }
`;

const ForgotLink = styled.a`
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const LoginButton = styled(motion.button)`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3), 0 2px 4px -1px rgba(139, 92, 246, 0.2);
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #9ca3af;
  font-size: 0.9rem;

  &::before, &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e5e7eb;
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }
`;

const SocialLogin = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SocialButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #d1d5db;
    background: #f9fafb;
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: #4b5563;

  a {
    color: #8b5cf6;
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const FranchiseLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    remember: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle login logic here
    console.log('Login submitted:', credentials);
  };

  return (
    <FranchiseLoginContainer>
      <VisualSection
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <VisualImage
          initial={{ scale: 1 }}
          animate={{ scale: 1.05 }}
          transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
        />
        <VisualContent>
          <VisualTitle>Franchise Partner Portal</VisualTitle>
          <VisualText>
            Access your franchise dashboard to manage locations, view performance metrics,
            and access exclusive resources for our valued partners.
          </VisualText>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <VisualText>
              New to our franchise network? Contact us to learn about partnership opportunities.
            </VisualText>
          </motion.div>
        </VisualContent>
      </VisualSection>

      <LoginSection>
        <LoginCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <LoginHeader>
            <LoginTitle>Franchise Login</LoginTitle>
            <LoginSubtitle>Welcome back! Please enter your credentials</LoginSubtitle>
          </LoginHeader>

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <InputLabel>Username</InputLabel>
              <InputIcon>
                <FaUser size={14} />
              </InputIcon>
              <InputField
                type="text"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                placeholder="Enter your username"
                required
              />
            </FormGroup>

            <FormGroup>
              <InputLabel>Password</InputLabel>
              <InputIcon>
                <FaLock size={14} />
              </InputIcon>
              <InputField
                type={showPassword ? "text" : "password"}
                name="password"
                value={credentials.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </PasswordToggle>
            </FormGroup>

            <RememberForgot>
              <RememberMe>
                <input
                  type="checkbox"
                  name="remember"
                  checked={credentials.remember}
                  onChange={handleChange}
                />
                Remember me
              </RememberMe>
              <ForgotLink href="#">Forgot password?</ForgotLink>
            </RememberForgot>

            <LoginButton
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Sign In <FaArrowRight />
            </LoginButton>
          </form>

          <Divider>or continue with</Divider>

          <SocialLogin>
            <SocialButton whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" alt="Google" width={18} />
            </SocialButton>
            <SocialButton whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Microsoft_Azure.svg" alt="Microsoft" width={18} />
            </SocialButton>
            <SocialButton whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/9/91/Octicons-mark-github.svg" alt="GitHub" width={18} />
            </SocialButton>
          </SocialLogin>

          <RegisterLink>
            Don't have an account? <a href="#">Request access</a>
          </RegisterLink>
        </LoginCard>
      </LoginSection>
    </FranchiseLoginContainer>
  );
};

export default FranchiseLogin;